import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, FilePlus, DownloadSimple, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

const LICENSE_LABELS = {
  PPL: "Özel Pilot",
  CPL: "Ticari Pilot",
  ATPL: "Havayolu Pilotu",
  CABIN_CREW: "Kabin Görevlisi",
  CABIN_SENIOR: "Kıdemli Kabin / Purser",
  CABIN_SAFETY: "Kabin Güvenlik Uzmanı",
  SAFETY_OFFICER: "Güvenlik Yetkilisi",
  EMERGENCY_RESPONSE: "Acil Müdahale",
  FIRE_SAFETY: "Yangın Güvenliği",
  FIRST_AID: "İlk Yardım",
  BLS: "Temel Yaşam Desteği",
  AVIATION_MEDICAL: "Havacılık Tıp",
  GROUND_STAFF: "Yer Hizmetleri",
  RAMP_AGENT: "Apron / Ramp Görevlisi",
  LOAD_CONTROL: "Yük Kontrol",
  DG_HANDLING: "Tehlikeli Madde",
  PASSENGER_SERVICE: "Yolcu Hizmetleri",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, revoked: 0, by_type: {} });
  const [certs, setCerts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [s, c] = await Promise.all([
        api.get("/certificates/stats"),
        api.get("/certificates", {
          params: { q: q || undefined, status_filter: statusFilter === "ALL" ? undefined : statusFilter },
        }),
      ]);
      setStats(s.data);
      setCerts(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kayıt defteri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    loadAll();
  };

  const onFilter = (s) => {
    setStatusFilter(s);
    setTimeout(loadAll, 0);
  };

  const onRevoke = async (id) => {
    try {
      await api.post(`/certificates/${id}/revoke`);
      toast.success("Sertifika iptal edildi.");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız.");
    }
  };

  const onReinstate = async (id) => {
    try {
      await api.post(`/certificates/${id}/reinstate`);
      toast.success("Sertifika yeniden etkinleştirildi.");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız.");
    }
  };

  const onAssignStudentId = async (c) => {
    const current = c.student_id || "";
    const sid = window.prompt(`"${c.holder_name}" için öğrenci numarası girin:`, current);
    if (sid == null) return;
    const trimmed = sid.trim();
    if (!trimmed) {
      toast.error("Öğrenci numarası boş olamaz.");
      return;
    }
    try {
      await api.patch(`/certificates/${c.id}/student-id`, { student_id: trimmed });
      toast.success(`Öğrenci numarası atandı: ${trimmed}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Atama başarısız.");
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="label-eyebrow">/ Sertifika Kaydı</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
            Sertifika defteri
          </h1>
          <p className="text-slate-600 mt-2">
            IAVA yetkilisi tarafından düzenlenen tüm sertifikaların canlı görünümü.
          </p>
        </div>
        <Link
          to="/admin/issue"
          data-testid="dashboard-issue-cert-button"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors font-medium"
        >
          <FilePlus size={16} weight="bold" /> Sertifika Oluştur
        </Link>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <Kpi label="Toplam Düzenlenen" value={stats.total} mono />
        <Kpi label="Aktif" value={stats.active} mono accent="text-emerald-700" />
        <Kpi label="Süresi Dolmuş" value={stats.expired} mono accent="text-amber-700" />
        <Kpi label="İptal Edilmiş" value={stats.revoked} mono accent="text-red-700" />
      </div>

      <div className="mt-px grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-slate-200 border border-slate-200 border-t-0">
        {Object.entries(stats.by_type || {}).map(([code, count]) => (
          <div key={code} className="bg-white p-5">
            <div className="label-eyebrow">{LICENSE_LABELS[code] || code}</div>
            <div className="font-mono-plex text-xl font-semibold text-slate-900 mt-2">
              {count}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex flex-1 min-w-[280px] max-w-xl">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="dashboard-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sertifika No, isim, uyruk ile ara…"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046]"
            />
          </div>
          <button className="px-4 bg-[#0B2046] text-white font-medium text-sm">Ara</button>
        </form>

        <div className="flex items-center gap-px bg-slate-200 border border-slate-200">
          {[
            { code: "ALL", label: "TÜMÜ" },
            { code: "ACTIVE", label: "AKTİF" },
            { code: "EXPIRED", label: "SÜRESİ DOLMUŞ" },
            { code: "REVOKED", label: "İPTAL" },
          ].map((s) => (
            <button
              key={s.code}
              data-testid={`filter-${s.code.toLowerCase()}`}
              onClick={() => onFilter(s.code)}
              className={`px-3 py-2 text-xs font-mono-plex tracking-wider ${
                statusFilter === s.code ? "bg-[#0B2046] text-white" : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <button
          onClick={loadAll}
          className="px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 text-sm"
          data-testid="dashboard-refresh"
        >
          <ArrowsClockwise size={14} /> Yenile
        </button>
      </div>

      <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <Th>Sertifika №</Th>
              <Th>Sertifika Sahibi</Th>
              <Th>Lisans Türü</Th>
              <Th>Uyruk</Th>
              <Th>Düzenleme</Th>
              <Th>Geçerlilik</Th>
              <Th>Durum</Th>
              <Th className="text-right">İşlemler</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center label-eyebrow">Kayıtlar yükleniyor…</td></tr>
            )}
            {!loading && certs.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="label-eyebrow">Henüz sertifika yok</div>
                  <Link to="/admin/issue" className="mt-3 inline-block text-sm font-medium text-[#0B2046] underline underline-offset-4">
                    İlk sertifikanı oluştur →
                  </Link>
                </td>
              </tr>
            )}
            {certs.map((c) => (
              <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50">
                <Td mono testid="row-cert-number">{c.certificate_number}</Td>
                <Td>{c.holder_name}</Td>
                <Td>{LICENSE_LABELS[c.license_type] || c.license_type}</Td>
                <Td>{c.nationality}</Td>
                <Td mono>{c.issue_date}</Td>
                <Td mono>{c.expiry_date}</Td>
                <Td>
                  <StatusBadge status={c.status} />
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <a
                    href={`${API_BASE}/certificates/${c.certificate_number}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="PDF İndir"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                  >
                    <DownloadSimple size={14} /> PDF
                  </a>
                  <button
                    onClick={() => onAssignStudentId(c)}
                    data-testid={`assign-sid-${c.id}`}
                    title="Öğrenci numarası ata"
                    className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                  >
                    {c.student_id ? `№ ${c.student_id}` : "ID Ata"}
                  </button>
                  {c.status === "REVOKED" ? (
                    <button
                      onClick={() => onReinstate(c.id)}
                      data-testid={`reinstate-${c.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50"
                    >
                      <ArrowsClockwise size={14} /> Aktifleştir
                    </button>
                  ) : (
                    <button
                      onClick={() => onRevoke(c.id)}
                      data-testid={`revoke-${c.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                    >
                      <X size={14} /> İptal Et
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, mono, accent = "text-slate-900" }) {
  return (
    <div className="bg-white p-6">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-3 font-display text-4xl font-black tracking-tighter ${accent} ${mono ? "font-mono-plex" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left font-mono-plex font-medium text-[11px] uppercase tracking-wider px-4 py-3 ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, mono, className = "", testid }) {
  return (
    <td
      data-testid={testid}
      className={`px-4 py-3 ${mono ? "font-mono-plex" : ""} text-slate-800 ${className}`}
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const labelMap = {
    ACTIVE: "AKTİF",
    EXPIRED: "SÜRESİ DOLMUŞ",
    REVOKED: "İPTAL EDİLDİ",
  };
  const map = {
    ACTIVE: "bg-emerald-50 text-emerald-800 border-emerald-200",
    EXPIRED: "bg-amber-50 text-amber-800 border-amber-200",
    REVOKED: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${map[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
      {labelMap[status] || status}
    </span>
  );
}
